// @ts-nocheck
import { useState, useCallback } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Button,
  Checkbox,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import {
  IconCheck,
  IconChecks,
  IconSend,
  IconX,
} from '@tabler/icons-react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import {
  useTimesheetVerifications,
  useApproveTimesheet,
  useRejectTimesheet,
  useBulkApproveTimesheets,
} from '../../hooks/useTimesheetVerification';

interface TimesheetVerification {
  [key: string]: unknown;
  id: string;
  originalId: string;
  version: number;
  employeeId: string;
  employeeName: string;
  payPeriodId: string;
  status: string;
  totalHours: number;
  regularHours: number;
  overtimeHours: number;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  rejectionReason: string | null;
}

const STATUS_COLOR_MAP: Record<string, string> = {
  draft: 'gray',
  submitted: 'blue',
  approved: 'green',
  rejected: 'red',
  locked: 'violet',
};

export default function TimesheetVerificationPage() {
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const { data: rawData, loading: isLoading } = useTimesheetVerifications({
    status: statusFilter || undefined,
  });
  const data = (rawData ?? []) as unknown as TimesheetVerification[];

  const approveMutation = useApproveTimesheet();
  const rejectMutation = useRejectTimesheet();
  const bulkApproveMutation = useBulkApproveTimesheets();

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (!data) return;
    const submittedIds = data
      .filter((ts: TimesheetVerification) => ts.status === 'submitted')
      .map((ts: TimesheetVerification) => ts.id);
    setSelectedIds((prev) => {
      if (prev.size === submittedIds.length && submittedIds.every((id) => prev.has(id))) {
        return new Set();
      }
      return new Set(submittedIds);
    });
  }, [data]);

  const handleApprove = useCallback(
    (id: string) => {
      approveMutation.mutate({ id }, {
        onSuccess: () => {
          notifications.show({
            title: 'Timesheet Approved',
            message: 'Timesheet has been approved successfully.',
            color: 'green',
          });
          setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        },
        onError: (error: any) => {
          notifications.show({
            title: 'Approval Failed',
            message: error instanceof Error ? error.message : 'An error occurred.',
            color: 'red',
          });
        },
      });
    },
    [approveMutation],
  );

  const handleOpenReject = useCallback((id: string) => {
    setRejectTargetId(id);
    setRejectReason('');
    setRejectModalOpen(true);
  }, []);

  const handleConfirmReject = useCallback(() => {
    if (!rejectTargetId || !rejectReason.trim()) return;
    rejectMutation.mutate(
      { id: rejectTargetId, reason: rejectReason.trim() },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Timesheet Rejected',
            message: 'Timesheet has been rejected and returned to draft.',
            color: 'orange',
          });
          setRejectModalOpen(false);
          setRejectTargetId(null);
          setRejectReason('');
        },
        onError: (error: any) => {
          notifications.show({
            title: 'Rejection Failed',
            message: error instanceof Error ? error.message : 'An error occurred.',
            color: 'red',
          });
        },
      },
    );
  }, [rejectTargetId, rejectReason, rejectMutation]);

  const handleBulkApprove = useCallback(() => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    bulkApproveMutation.mutate({ ids }, {
      onSuccess: (result: any) => {
        const bulkResult = result?.bulkApproveTimesheets ?? { approved: 0, rejected: 0 };
        notifications.show({
          title: 'Bulk Approval Complete',
          message: `${bulkResult.approved} approved, ${bulkResult.rejected} failed.`,
          color: bulkResult.rejected > 0 ? 'orange' : 'green',
        });
        setSelectedIds(new Set());
      },
      onError: (error: any) => {
        notifications.show({
          title: 'Bulk Approval Failed',
          message: error instanceof Error ? error.message : 'An error occurred.',
          color: 'red',
        });
      },
    });
  }, [selectedIds, bulkApproveMutation]);

  const submittedCount = data?.filter((ts: TimesheetVerification) => ts.status === 'submitted').length ?? 0;

  const columns: Column<TimesheetVerification>[] = [
    {
      key: '__select',
      label: '',
      width: 40,
      render: (row) =>
        row.status === 'submitted' ? (
          <Checkbox
            checked={selectedIds.has(row.id)}
            onChange={() => handleToggleSelect(row.id)}
          />
        ) : null,
    },
    {
      key: 'employeeName',
      label: 'Employee',
      sortable: true,
    },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      render: (row) => <StatusBadge status={row.status} colorMap={STATUS_COLOR_MAP} />,
    },
    {
      key: 'totalHours',
      label: 'Total Hours',
      width: 110,
      sortable: true,
      render: (row) => row.totalHours.toFixed(2),
    },
    {
      key: 'regularHours',
      label: 'Regular',
      width: 100,
      render: (row) => row.regularHours.toFixed(2),
    },
    {
      key: 'overtimeHours',
      label: 'Overtime',
      width: 100,
      render: (row) => (
        <Text c={row.overtimeHours > 0 ? 'orange' : undefined} size="sm">
          {row.overtimeHours.toFixed(2)}
        </Text>
      ),
    },
    {
      key: 'version',
      label: 'Version',
      width: 80,
      render: (row) => `v${row.version}`,
    },
    {
      key: 'submittedAt',
      label: 'Submitted',
      width: 140,
      render: (row) =>
        row.submittedAt ? new Date(row.submittedAt).toLocaleDateString() : '--',
    },
    {
      key: 'approvedAt',
      label: 'Approved',
      width: 140,
      render: (row) =>
        row.approvedAt ? new Date(row.approvedAt).toLocaleDateString() : '--',
    },
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title="Timesheet Verification"
        subtitle={
          data
            ? `${data.length} timesheets | ${submittedCount} pending approval`
            : undefined
        }
        actions={
          <Button
            icon={<IconChecks size={16} />}
            disabled={selectedIds.size === 0}
            loading={bulkApproveMutation.isPending}
            onClick={handleBulkApprove}
          >
            Bulk Approve ({selectedIds.size})
          </Button>
        }
      />

      <Group gap="sm">
        <Select
          placeholder="Filter by status"
          clearable
          value={statusFilter}
          onChange={setStatusFilter}
          data={[
            { value: 'draft', label: 'Draft' },
            { value: 'submitted', label: 'Submitted' },
            { value: 'approved', label: 'Approved' },
            { value: 'rejected', label: 'Rejected' },
            { value: 'locked', label: 'Locked' },
          ]}
          w={180}
        />
        {submittedCount > 0 && (
          <Button variant="subtle" size="sm" onClick={handleSelectAll}>
            {selectedIds.size === submittedCount ? 'Deselect All' : 'Select All Submitted'}
          </Button>
        )}
      </Group>

      <DataTable<TimesheetVerification>
        columns={columns}
        data={(data as TimesheetVerification[]) ?? []}
        loading={isLoading}
        pagination={
          data
            ? {
                page: 1,
                pageSize: 50,
                total: data.length,
                onPageChange: () => {},
              }
            : undefined
        }
        getRowKey={(row) => row.id}
        emptyMessage="No timesheets found for this period"
        actions={(row) =>
          row.status === 'submitted' ? (
            <Group gap="xs" wrap="nowrap">
              <Button
                size="xs"
                variant="light"
                color="green"
                icon={<IconCheck size={14} />}
                loading={approveMutation.isPending}
                onClick={() => handleApprove(row.id)}
              >
                Approve
              </Button>
              <Button
                size="xs"
                variant="light"
                color="red"
                icon={<IconX size={14} />}
                onClick={() => handleOpenReject(row.id)}
              >
                Reject
              </Button>
            </Group>
          ) : row.status === 'draft' ? (
            <Button
              size="xs"
              variant="light"
              icon={<IconSend size={14} />}
              disabled
            >
              Not Submitted
            </Button>
          ) : null
        }
      />

      {/* Reject Modal */}
      <Modal
        opened={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        title="Reject Timesheet"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Rejecting this timesheet will return it to draft status. The employee will need
            to correct and resubmit. A new version will be created.
          </Text>
          <Textarea
            label="Rejection Reason"
            placeholder="Explain why this timesheet is being rejected..."
            required
            minRows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setRejectModalOpen(false)}
              disabled={rejectMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleConfirmReject}
              loading={rejectMutation.isPending}
              disabled={!rejectReason.trim()}
            >
              Reject Timesheet
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
